import React from 'react';

import { DataTableRow } from 'cpool-components/general/DataTable';
import { LOAN_REQUEST_OFFER_SOURCES, OFFER_ACCEPTANCE_STATUSES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { BankLogoWithDescription } from 'src/components/BankLogoWithDescription';

import type { OfferSource } from './types';
import { OfferAcceptButton } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RequestConfiguration/OfferAcceptButton.tsx';
import { OfferExtraInfo } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RequestConfiguration/OfferExtraInfo';
import { ApplicantTrafficLight } from '~/features/leadDetails/components/BankCreditRequest/OfferHistory/ApplicantTrafficLight.tsx';
import { useOfferAcceptanceStatusByOfferId } from '~/features/leadDetails/hooks/useOfferAcceptanceStatusByOfferId';
import { currencyFormat, dateFormat, numberFormat } from '~/lib/i18n.ts';
import { cn } from '~/utils/cnTailwind.ts';

export type RSV = 'ARBEITSLOSIGKEIT' | 'ARBEITSUNFAEHIGKEIT' | 'LEBEN';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
    isOdd: boolean;
    createdAt: string;
    requestId?: string;
    insuranceApplicant1: Array<RSV>;
    insuranceApplicant2: Array<RSV>;
    kreditbetrag?: number;
    laufZeit?: number;
    monatlicheRate?: number;
    zinsen?: number;
} & OfferSource;

export const BankOfferResultsRow: React.FC<Props> = (props) => {
    const {
        createdAt,
        insuranceApplicant1,
        insuranceApplicant2,
        isOdd,
        kreditbetrag,
        laufZeit,
        lead,
        monatlicheRate,
        offer,
        requestId,
        zinsen,
    } = props;
    const { t } = useTranslation();

    const { data: acceptanceStatus } = useOfferAcceptanceStatusByOfferId({
        loanRequestId: lead.id,
        offerId: offer.id,
        source: props.source,
    });

    const offersDate = createdAt ? dateFormat(new Date(createdAt)) : '';
    let bgColorClass = '';
    if (acceptanceStatus === OFFER_ACCEPTANCE_STATUSES.FAILURE) {
        bgColorClass = 'odd:bg-red-light even:bg-red-dark';
    } else if (acceptanceStatus === OFFER_ACCEPTANCE_STATUSES.SUCCESS) {
        bgColorClass = 'odd:bg-green-light even:bg-green-dark';
    }

    return (
        <DataTableRow
            payload={{ id: offer.id }}
            className={cn('text-center text-sm text-midnight [&>td]:px-3 [&>td]:py-2.5', bgColorClass)}
        >
            <td>
                <BankLogoWithDescription
                    description={offer.ratenkredit?.produktbezeichnung}
                    bankLogo={props.offer.ratenkredit?.produktanbieter?.logo?.svg}
                    bankName={
                        props.source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE
                            ? String(props.offer.ratenkredit?.produktanbieter?.name ?? '')
                            : LOAN_REQUEST_OFFER_SOURCES.MANUAL
                    }
                    imgClassName="mx-auto inline h-10 w-20"
                />
            </td>
            <td>{offersDate}</td>
            <td>{kreditbetrag ? currencyFormat(kreditbetrag) : t('BANK_CREDIT_REQUEST.NO_VALUE_GIVEN')}</td>
            <td className="whitespace-nowrap">
                {laufZeit?.toString() || t('BANK_CREDIT_REQUEST.NO_VALUE_GIVEN')}{' '}
                {t('BANK_CREDIT_REQUEST.OFFERS_TABLE.MONATE')}
            </td>
            <td>{monatlicheRate ? currencyFormat(monatlicheRate) : t('BANK_CREDIT_REQUEST.NO_VALUE_GIVEN')}</td>
            <td>{zinsen ? numberFormat(zinsen) : t('BANK_CREDIT_REQUEST.NO_VALUE_GIVEN')}%</td>
            <td>
                <div className="flex items-center justify-center">
                    <ApplicantTrafficLight
                        isOdd={isOdd}
                        acceptanceStatus={acceptanceStatus}
                        applicantInsurances={insuranceApplicant1}
                    />
                    <div className="mx-1 text-xl">/</div>
                    <ApplicantTrafficLight
                        isOdd={isOdd}
                        acceptanceStatus={acceptanceStatus}
                        applicantInsurances={insuranceApplicant2}
                    />
                </div>
            </td>
            <td>
                {props.source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE && (
                    <OfferExtraInfo offer={props.offer} routeControlled source={props.source} />
                )}
            </td>
            <td>
                {props.source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE && (
                    <OfferAcceptButton
                        offer={props.offer}
                        source={props.source}
                        lead={lead}
                        currentRequestId={requestId}
                        isOdd={isOdd}
                        routeControlled
                    />
                )}
            </td>
        </DataTableRow>
    );
};
